import { Disclosure } from '@headlessui/react';
import { Fragment } from 'react';
import { Icon } from '../../../../../core/components/atoms/Icon';
import { Typography } from '../../../../../core/components/molecules/Typography';
import { useExerciseStore } from '../../../stores/exercisesStore';
import InnerCheckbox from '../../exercises/components/innerCheckbox';

interface IExerciseContentFilter {
  compact?: boolean;
}

export default function ExerciseContentFilter({ compact = false }: IExerciseContentFilter) {
  const {
    favorite,
    difficulty,
    unanswered,
    rightAnswer,
    wrongAnswer,
    questionOptions,
    isEnem,
    setFavorite,
    setDifficulty,
    setUnanswered,
    setRightAnswer,
    setWrongAnswer,
    setQuestionOptions,
    setIsEnem
  } = useExerciseStore();

  const difficultyHandler = (level: string) => {
    if (difficulty.indexOf(level) >= 0) {
      const array = [...difficulty];

      array.splice(difficulty.indexOf(level), 1);
      setDifficulty(array);
    } else {
      const array = [...difficulty];
      array.push(level);

      setDifficulty(array);
    }
  };

  const isEnemHandler = (option: string) => {
    if (isEnem.indexOf(option) >= 0) {
      const array = [...isEnem];

      array.splice(isEnem.indexOf(option), 1);
      setIsEnem(array);
    } else {
      const array = [...isEnem];
      array.push(option);

      setIsEnem(array);
    }
  };

  const questionHandler = (type: string) => {
    const aux = [...questionOptions];
    const idx = aux.find((it) => it === type);

    if(idx) {
      const newArray = aux.filter((it) => it !== type)
      setQuestionOptions(newArray);
    } else {
      aux.push(type);
      setQuestionOptions(aux);
    }
  };

  const sectionButtonClass = `flex w-full items-center justify-between text-left ${compact ? 'py-1' : ''}`;
  const sectionPanelClass = `${compact ? 'mt-2 gap-md pb-2 pl-3' : 'mt-5 gap-xl pb-3 pl-4'} flex w-full flex-col`;
  const titleSize = compact ? 'h5' : 'h4';

  return (
    <Fragment>
      <div className={`flex w-full flex-col gap-md overflow-x-auto ${compact ? 'px-1 py-1' : 'px-4 py-5 xs:h-[45vh] lg:h-[75vh]'}`}>
        <Disclosure defaultOpen>
          <Disclosure.Panel className={`${compact ? 'gap-md pb-2' : 'gap-xl pb-3'} flex w-full flex-col`}>
            <InnerCheckbox
              onChange={() => setFavorite(!favorite)}
              label='Favoritados'
              checked={favorite}
              id='favorite'
              value={'favorite'}
            />
          </Disclosure.Panel>
        </Disclosure>
        <Disclosure defaultOpen>
          {({ open }) => (
            <Fragment>
              <Disclosure.Button className={sectionButtonClass}>
                <Typography size={titleSize} type='title' className='text-left'>
                  Tipo de questão
                </Typography>
                <Icon
                  Icon='expand_more'
                  type='material'
                  className={`${open ? 'rotate-180 transform' : ''} h-5 w-5 text-mono-color-black`}
                />
              </Disclosure.Button>
              <Disclosure.Panel className={sectionPanelClass}>
                <InnerCheckbox
                  onChange={() => isEnemHandler('enem')}
                  label='Enem'
                  checked={isEnem.includes('enem')}
                  id='type-enem'
                  value={'enem'}
                />
                <InnerCheckbox
                  onChange={() => isEnemHandler('vestibulares')}
                  label='Vestibulares'
                  checked={isEnem.includes('vestibulares')}
                  id='type-vestibulares'
                  value={'vestibulares'}
                />
              </Disclosure.Panel>
            </Fragment>
          )}
        </Disclosure>
        <Disclosure defaultOpen>
          {({ open }) => (
            <Fragment>
              <Disclosure.Button className={sectionButtonClass}>
                <Typography size={titleSize} type='title' className='text-left'>
                  Resolução
                </Typography>
                <Icon
                  Icon='expand_more'
                  type='material'
                  className={`${open ? 'rotate-180 transform' : ''} h-5 w-5 text-mono-color-black`}
                />
              </Disclosure.Button>
              <Disclosure.Panel className={sectionPanelClass}>
                <InnerCheckbox
                  onChange={() => setUnanswered(!unanswered)}
                  label='Não respondidos'
                  checked={unanswered}
                  id='resolution-notanswered'
                  value={'unanswered'}
                />
                <InnerCheckbox
                  onChange={() => setWrongAnswer(!wrongAnswer)}
                  label='Respostas erradas'
                  checked={wrongAnswer}
                  id='resolution-wrong'
                  value='wrongAnswer'
                />
                <InnerCheckbox
                  onChange={() => setRightAnswer(!rightAnswer)}
                  label='Respostas certas'
                  checked={rightAnswer}
                  id='resolution-right'
                  value='rightAnswer'
                />
              </Disclosure.Panel>
            </Fragment>
          )}
        </Disclosure>
        <Disclosure defaultOpen>
          {({ open }) => (
            <Fragment>
              <Disclosure.Button className={sectionButtonClass}>
                <Typography size={titleSize} type='title' className='text-left'>
                  Nível de dificuldade
                </Typography>
                <Icon
                  Icon='expand_more'
                  type='material'
                  className={`${open ? 'rotate-180 transform' : ''} h-5 w-5 text-mono-color-black`}
                />
              </Disclosure.Button>
              <Disclosure.Panel className={sectionPanelClass}>
                <InnerCheckbox
                  onChange={() => difficultyHandler('easy')}
                  label='Fácil'
                  checked={difficulty.indexOf('easy') >= 0}
                  id='difficulty-easy'
                  value='easy'
                />
                <InnerCheckbox
                  onChange={() => difficultyHandler('medium')}
                  label='Médio'
                  checked={difficulty.indexOf('medium') >= 0}
                  id='difficulty-medium'
                  value='medium'
                />
                <InnerCheckbox
                  onChange={() => difficultyHandler('hard')}
                  label='Difícil'
                  checked={difficulty.indexOf('hard') >= 0}
                  id='difficulty-hard'
                  value='hard'
                />
                <InnerCheckbox
                  onChange={() => difficultyHandler('random')}
                  label='Aleatório'
                  checked={difficulty.indexOf('random') >= 0}
                  id='difficulty-random'
                  value='random'
                />
              </Disclosure.Panel>
            </Fragment>
          )}
        </Disclosure>
        <Disclosure defaultOpen>
          {({ open }) => (
            <Fragment>
              <Disclosure.Button className={sectionButtonClass}>
                <Typography size={titleSize} type='title' className='text-left'>
                  Tipo de exercício
                </Typography>
                <Icon
                  Icon='expand_more'
                  type='material'
                  className={`${open ? 'rotate-180 transform' : ''} h-5 w-5 text-mono-color-black`}
                />
              </Disclosure.Button>
              <Disclosure.Panel className={sectionPanelClass}>
                <InnerCheckbox
                  onChange={() => questionHandler('single_answer')}
                  label='Resposta única'
                  checked={questionOptions.indexOf('single_answer') >= 0}
                  id='type-single-answer'
                  value='single_answer'
                />
                <InnerCheckbox
                  onChange={() => questionHandler('multiple_answer')}
                  label='Resposta somatória'
                  checked={questionOptions.indexOf('multiple_answer') >= 0}
                  id='type-multiple-answer'
                  value='multiple_answer'
                />
                <InnerCheckbox
                  onChange={() => questionHandler('discursive')}
                  label='Resposta discursiva'
                  checked={questionOptions.indexOf('discursive') >= 0}
                  id='type-discursive'
                  value='discursive'
                />
              </Disclosure.Panel>
            </Fragment>
          )}
        </Disclosure>
      </div>
    </Fragment>
  );
}
